"use client";

import { useState } from "react";
import { EditDialog } from "@/components/portfolio/EditDialog";
import { buttonClass } from "@/components/ui";
import type { CaseStudy } from "@/lib/portfolio/demo";

/**
 * "View case study" CTA on a project card — opens the fuller Situation /
 * Approach / Impact write-up in a dialog (EditDialog's scrollable, wider
 * variant of the pitch-dialog shell). Demo-only today: the parent only passes
 * `caseStudy` for the sample page's first two projects.
 */
export function CaseStudyButton({
  projectName,
  caseStudy,
}: {
  projectName: string;
  caseStudy: CaseStudy;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        className={`mt-4 ${buttonClass("secondary", "sm")}`}
      >
        View case study →
      </button>

      {open ? (
        <EditDialog
          kicker="Case study"
          title={projectName}
          onClose={() => setOpen(false)}
        >
          <p className="text-[15px] leading-relaxed text-neutral-700">
            {caseStudy.summary}
          </p>

          {caseStudy.stats.length > 0 ? (
            <dl className="mt-5 grid grid-cols-3 gap-3 border-y border-neutral-100 py-4">
              {caseStudy.stats.map((s) => (
                <div key={s.label}>
                  <dd className="display-calm text-xl font-semibold text-ink">
                    {s.value}
                  </dd>
                  <dt className="telex mt-1 text-[8.5px] text-neutral-500">
                    {s.label}
                  </dt>
                </div>
              ))}
            </dl>
          ) : null}

          <div className="mt-5 space-y-5">
            {caseStudy.sections.map((s) => (
              <div key={s.heading}>
                <h3 className="telex text-[9.5px] text-brand-700">
                  {s.heading}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-neutral-700">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </EditDialog>
      ) : null}
    </>
  );
}
